import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faClose, faMartiniGlassCitrus } from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import GetSpace from "../../../utils/space"
import { companyMemory, tableMemory } from "../../../Objects/Memory/Memory"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { CanView } from "../../../components/CanView/CanView"
import { Permissions } from "../../../Enum/PermissionsType"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import { EKitchenBarMode } from "../../../Enum/EKitchenBarMode"
import { EZeroValueSelectionOptionDisplayMode } from "../../../Enum/EZeroValueSelectionOptionDisplayMode"
import { SimpleToggle } from "../../../components/SimpleToggle/SimpleToggle"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"

interface OperationalSettingsPopupProps {
  onClose: () => void
}

export const OperationalSettingsPopup: React.FC<OperationalSettingsPopupProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { handleSaveCompany, handleSaveTableSelfService, handleSaveDefaultTable } = useSaveCompany()

  const { handleGetCompany } = useGetCompany()

  const {
    handleInitialValue,
    handleSubmitForm,
    kitchenBarMode,
    setKitchenBarMode,
    zeroValueSelectionOptionDisplayMode,
    setZeroValueSelectionOptionDisplayMode,
    isPublicMenuOrderingEnabled,
    setIsPublicMenuOrderingEnabled,
  } = useCreateCompany()

  const [isTableSelfServiceEnabled, setTableSelfServiceEnabled] = useState(companyMemory.isTableSelfServiceEnabled)

  const [isTableSelfServiceOrderingEnabled, setTableSelfServiceOrderingEnabled] = useState(
    companyMemory.isTableSelfServiceOrderingEnabled,
  )

  const [defaultTableId, setDefaultTableId] = useState<number | null>(companyMemory.defaultTableId)

  const activeTables = tableMemory.listData
    .filter((table) => table.status === EEnableStatusType.Active)
    .sort((first, second) => first.number - second.number)

  useEffect(() => {
    handleInitialValue(companyMemory)

    setTableSelfServiceEnabled(companyMemory.isTableSelfServiceEnabled)

    setTableSelfServiceOrderingEnabled(companyMemory.isTableSelfServiceOrderingEnabled)

    setDefaultTableId(
      tableMemory.listData.some(
        (table) => table.id === companyMemory.defaultTableId && table.status === EEnableStatusType.Active,
      )
        ? companyMemory.defaultTableId
        : null,
    )
  }, [])

  const handleTrySave = () => {
    const data = handleSubmitForm()

    handleSaveCompany(data, async () => {
      const didSaveDefaultTable = await handleSaveDefaultTable(companyMemory.id, defaultTableId)

      if (!didSaveDefaultTable) {
        return
      }

      await handleSaveTableSelfService(
        companyMemory.id,
        isTableSelfServiceEnabled,
        isTableSelfServiceOrderingEnabled,
        () => {
          handleGetCompany(() => handleInitialValue(companyMemory))
          onClose()
        },
      )
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("operational.title")}
              icon={faMartiniGlassCitrus}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.myrestaurant.mycompany.operationalsettingspopup.1"
                    variant="global"
                    title={t("operational.help_title")}
                    description={t("operational.help_description")}
                    bullets={t("operational.help_bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="flex flex-col gap-4 p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()

                handleTrySave()
              }}
            >
              <GenericDropDown
                legend={t("operational.kitchen_bar_mode")}
                initialState={kitchenBarMode.toString()}
                data={[
                  {
                    value: EKitchenBarMode.Combined.toString(),
                    label: t("operational.mode_combined"),
                  },
                  {
                    value: EKitchenBarMode.Separated.toString(),
                    label: t("operational.mode_separated"),
                  },
                ]}
                handlerFuncion={(e) => {
                  const mode = Number(e.target.value) as EKitchenBarMode

                  setKitchenBarMode(mode)
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.select.kitchenBarMode"
              />

              <div className={defaultStyle.divisionBar}></div>

              <SimpleToggle
                title={t("operational.public_menu_ordering.enabled")}
                value={isPublicMenuOrderingEnabled}
                onChange={setIsPublicMenuOrderingEnabled}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.toggle.publicMenuOrderingEnabled"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">
                  {isPublicMenuOrderingEnabled
                    ? t("operational.public_menu_ordering.enabled_hint")
                    : t("operational.public_menu_ordering.disabled_hint")}
                </p>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <GenericDropDown
                legend={t("operational.zero_value_option_display.label")}
                initialState={zeroValueSelectionOptionDisplayMode.toString()}
                data={[
                  {
                    value: EZeroValueSelectionOptionDisplayMode.ShowFree.toString(),
                    label: t("operational.zero_value_option_display.mode_show_free"),
                  },
                  {
                    value: EZeroValueSelectionOptionDisplayMode.ShowNothing.toString(),
                    label: t("operational.zero_value_option_display.mode_show_nothing"),
                  },
                  {
                    value: EZeroValueSelectionOptionDisplayMode.ShowBasePrice.toString(),
                    label: t("operational.zero_value_option_display.mode_show_base_price"),
                  },
                ]}
                handlerFuncion={(e) => {
                  const mode = Number(e.target.value) as EZeroValueSelectionOptionDisplayMode

                  setZeroValueSelectionOptionDisplayMode(mode)
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.select.zeroValueSelectionOptionDisplayMode"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">{t("operational.zero_value_option_display.hint")}</p>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <GenericDropDown
                legend={t("operational.default_table.label")}
                initialState={defaultTableId?.toString() ?? ""}
                data={[
                  { value: "", label: t("operational.default_table.none") },
                  ...activeTables.map((table) => ({
                    value: table.id.toString(),
                    label: t("operational.default_table.option", { number: table.number }),
                  })),
                ]}
                handlerFuncion={(event) => {
                  setDefaultTableId(event.target.value ? Number(event.target.value) : null)
                }}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.select.select"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">{t("operational.default_table.hint")}</p>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <SimpleToggle
                title={t("operational.table_self_service.enabled")}
                value={isTableSelfServiceEnabled}
                onChange={setTableSelfServiceEnabled}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.toggle.enabled"
              />

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <p className="px-2 text-xs text-slate-500">{t("operational.table_self_service.enabled_hint")}</p>
              </Animate>

              {isTableSelfServiceEnabled && (
                <>
                  <SimpleToggle
                    title={t("operational.table_self_service.ordering_enabled")}
                    value={isTableSelfServiceOrderingEnabled}
                    onChange={setTableSelfServiceOrderingEnabled}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.toggle.orderingEnabled"
                  />

                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                    <p className="px-2 text-xs text-slate-500">
                      {t("operational.table_self_service.ordering_enabled_hint")}
                    </p>
                  </Animate>
                </>
              )}

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.operationalSettingsPopup.button.save"
                  />
                </CanView>
              </section>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
